import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { FuseAlertComponent } from '@fuse/components/alert';

/**
 * Custom validator to check if passwords match
 */
function passwordsMatchValidator(control: AbstractControl): ValidationErrors | null {
    const password = control.get('password');
    const confirmPassword = control.get('confirmPassword');

    if (!password || !confirmPassword) {
        return null;
    }

    // Only validate if both fields have values
    if (password.value && confirmPassword.value && password.value !== confirmPassword.value) {
        return { passwordsMismatch: true };
    }

    return null;
}

/**
 * Custom validator to check password strength
 * Requirements: min 8 chars, uppercase, lowercase, number, special character
 */
function passwordStrengthValidator(control: AbstractControl): ValidationErrors | null {
    const password = control.value;
    
    if (!password || password === '') {
        return null; // Don't validate empty passwords (handled by required validator)
    }

    const errors: any = {};

    if (password.length < 8) {
        errors.passwordTooShort = true;
    }
    if (!/[A-Z]/.test(password)) {
        errors.noUpperCase = true;
    }
    if (!/[a-z]/.test(password)) {
        errors.noLowerCase = true;
    }
    if (!/[0-9]/.test(password)) {
        errors.noNumber = true;
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
        errors.noSpecialChar = true;
    }

    return Object.keys(errors).length ? errors : null;
}

@Component({
    selector: 'user-profile-dialog',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        FuseAlertComponent
    ],
    template: `
        <div class="flex flex-col max-w-screen-xl w-full">
            <!-- Header -->
            <div class="flex items-center justify-between p-6 pb-4">
                <h2 mat-dialog-title class="text-2xl font-semibold m-0">Actualizar Perfil</h2>
                <button mat-icon-button [mat-dialog-close]="false">
                    <mat-icon [svgIcon]="'heroicons_outline:x-mark'"></mat-icon>
                </button>
            </div>

            <!-- Content -->
            <mat-dialog-content class="p-6 pt-0">
                <!-- Alert -->
                <fuse-alert
                    *ngIf="showAlert"
                    [type]="alert.type"
                    [appearance]="'outline'"
                    [showIcon]="true"
                    [dismissible]="false"
                    class="mb-4">
                    {{alert.message}}
                </fuse-alert>

                <form [formGroup]="profileForm" class="flex flex-col">
                    <!-- Name -->
                    <mat-form-field class="w-full">
                        <mat-label>Nombre completo</mat-label>
                        <input
                            matInput
                            formControlName="name"
                            placeholder="Escribe tu nombre completo">
                        <mat-icon
                            matPrefix
                            [svgIcon]="'heroicons_outline:user'"></mat-icon>
                        <mat-error *ngIf="profileForm.get('name').hasError('required')">
                            El nombre es obligatorio
                        </mat-error>
                    </mat-form-field>

                    <!-- Email -->
                    <mat-form-field class="w-full">
                        <mat-label>Email</mat-label>
                        <input
                            matInput
                            type="email"
                            formControlName="email"
                            placeholder="tu@email.com">
                        <mat-icon
                            matPrefix
                            [svgIcon]="'heroicons_outline:envelope'"></mat-icon>
                        <mat-error *ngIf="profileForm.get('email').hasError('required')">
                            El email es obligatorio
                        </mat-error>
                        <mat-error *ngIf="profileForm.get('email').hasError('email')">
                            Email no válido
                        </mat-error>
                    </mat-form-field>

                    <!-- Password (optional) -->
                    <mat-form-field class="w-full">
                        <mat-label>Nueva contraseña (opcional)</mat-label>
                        <input
                            matInput
                            [type]="hidePassword ? 'password' : 'text'"
                            formControlName="password"
                            placeholder="Dejar vacío para mantener la actual">
                        <mat-icon
                            matPrefix
                            [svgIcon]="'heroicons_outline:lock-closed'"></mat-icon>
                        <button
                            mat-icon-button
                            matSuffix
                            type="button"
                            (click)="hidePassword = !hidePassword">
                            <mat-icon [svgIcon]="hidePassword ? 'heroicons_outline:eye' : 'heroicons_outline:eye-slash'"></mat-icon>
                        </button>
                        <mat-error *ngIf="profileForm.get('password').invalid && profileForm.get('password').value">
                            La contraseña no cumple los requisitos
                        </mat-error>
                    </mat-form-field>

                    <!-- Password Requirements Hint -->
                    <div *ngIf="profileForm.get('password').value" class="mb-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg text-sm">
                        <p class="font-medium mb-2 text-gray-700 dark:text-gray-300">Requisitos de contraseña:</p>
                        <ul class="space-y-1">
                            <li [class]="profileForm.get('password').hasError('passwordTooShort') ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'">
                                <mat-icon class="icon-size-4 align-middle" [svgIcon]="profileForm.get('password').hasError('passwordTooShort') ? 'heroicons_solid:x-circle' : 'heroicons_solid:check-circle'"></mat-icon>
                                Mínimo 8 caracteres
                            </li>
                            <li [class]="profileForm.get('password').hasError('noUpperCase') ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'">
                                <mat-icon class="icon-size-4 align-middle" [svgIcon]="profileForm.get('password').hasError('noUpperCase') ? 'heroicons_solid:x-circle' : 'heroicons_solid:check-circle'"></mat-icon>
                                Al menos una mayúscula
                            </li>
                            <li [class]="profileForm.get('password').hasError('noLowerCase') ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'">
                                <mat-icon class="icon-size-4 align-middle" [svgIcon]="profileForm.get('password').hasError('noLowerCase') ? 'heroicons_solid:x-circle' : 'heroicons_solid:check-circle'"></mat-icon>
                                Al menos una minúscula
                            </li>
                            <li [class]="profileForm.get('password').hasError('noNumber') ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'">
                                <mat-icon class="icon-size-4 align-middle" [svgIcon]="profileForm.get('password').hasError('noNumber') ? 'heroicons_solid:x-circle' : 'heroicons_solid:check-circle'"></mat-icon>
                                Al menos un número
                            </li>
                            <li [class]="profileForm.get('password').hasError('noSpecialChar') ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'">
                                <mat-icon class="icon-size-4 align-middle" [svgIcon]="profileForm.get('password').hasError('noSpecialChar') ? 'heroicons_solid:x-circle' : 'heroicons_solid:check-circle'"></mat-icon>
                                Un carácter especial (como !#$%&amp;*...)
                            </li>
                        </ul>
                    </div>

                    <!-- Confirm Password -->
                    <mat-form-field class="w-full">
                        <mat-label>Confirmar contraseña</mat-label>
                        <input
                            matInput
                            [type]="hideConfirmPassword ? 'password' : 'text'"
                            formControlName="confirmPassword"
                            placeholder="Repite la contraseña">
                        <mat-icon
                            matPrefix
                            [svgIcon]="'heroicons_outline:lock-closed'"></mat-icon>
                        <button
                            mat-icon-button
                            matSuffix
                            type="button"
                            (click)="hideConfirmPassword = !hideConfirmPassword">
                            <mat-icon [svgIcon]="hideConfirmPassword ? 'heroicons_outline:eye' : 'heroicons_outline:eye-slash'"></mat-icon>
                        </button>
                        <mat-error *ngIf="profileForm.hasError('passwordsMismatch') && profileForm.get('confirmPassword').value">
                            Las contraseñas no coinciden
                        </mat-error>
                    </mat-form-field>

                    <!-- Info -->
                    <div class="flex items-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                        <mat-icon
                            class="text-blue-600 dark:text-blue-400 mr-3"
                            [svgIcon]="'heroicons_outline:information-circle'"></mat-icon>
                        <p class="text-sm text-blue-700 dark:text-blue-300 m-0">
                            Tu rol <strong>({{getRoleName(data.user.role)}})</strong> no puede ser modificado desde aquí.
                        </p>
                    </div>
                </form>
            </mat-dialog-content>

            <!-- Actions -->
            <mat-dialog-actions class="flex items-center justify-end p-6 pt-4 border-t">
                <button
                    mat-button
                    [mat-dialog-close]="false"
                    type="button">
                    Cancelar
                </button>
                <button
                    mat-flat-button
                    color="primary"
                    [disabled]="profileForm.invalid || isSaving"
                    (click)="saveProfile()">
                    <mat-icon
                        *ngIf="isSaving"
                        class="animate-spin mr-2"
                        [svgIcon]="'heroicons_outline:arrow-path'"></mat-icon>
                    {{isSaving ? 'Guardando...' : 'Guardar cambios'}}
                </button>
            </mat-dialog-actions>
        </div>
    `
})
export class UserProfileDialogComponent implements OnInit {
    profileForm: FormGroup;
    hidePassword = true;
    hideConfirmPassword = true;
    isSaving = false;
    showAlert = false;
    alert: { type: 'success' | 'error'; message: string } = { type: 'success', message: '' };

    constructor(
        public dialogRef: MatDialogRef<UserProfileDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: { user: User },
        private _formBuilder: FormBuilder,
        private _userService: UserService
    ) {}

    ngOnInit(): void {
        // Initialize the form
        this.profileForm = this._formBuilder.group({
            name: [this.data.user.name, [Validators.required]],
            email: [this.data.user.email, [Validators.required, Validators.email]],
            password: ['', [passwordStrengthValidator]],
            confirmPassword: ['', []]
        }, { 
            validators: passwordsMatchValidator 
        });

        // Re-validate on password changes
        this.profileForm.get('password').valueChanges.subscribe(() => {
            this.profileForm.get('confirmPassword').updateValueAndValidity({ onlySelf: true });
        });

        this.profileForm.get('confirmPassword').valueChanges.subscribe(() => {
            this.profileForm.updateValueAndValidity({ onlySelf: true });
        });
    }

    /**
     * Save profile changes
     */
    saveProfile(): void {
        if (this.profileForm.invalid) {
            return;
        }

        this.isSaving = true;
        this.showAlert = false;

        // Get form values
        const formValue = this.profileForm.value;
        const profileData: { name: string; email: string; password?: string; password_confirmation?: string } = {
            name: formValue.name,
            email: formValue.email
        };

        // Only include password if it was provided and passwords match
        if (formValue.password && formValue.password.trim() !== '' && !this.profileForm.hasError('passwordsMismatch')) {
            profileData.password = formValue.password;
            profileData.password_confirmation = formValue.confirmPassword;
        }

        // Call the API to update profile
        this._userService.updateProfile(this.data.user.id, profileData).subscribe({
            next: (updatedUser) => {
                this.isSaving = false;
                this.showAlert = true;
                this.alert = {
                    type: 'success',
                    message: '✅ Perfil actualizado correctamente'
                };

                // Close dialog after a short delay
                setTimeout(() => {
                    this.dialogRef.close(true);
                }, 1500);
            },
            error: (error) => {
                this.isSaving = false;
                this.showAlert = true;
                this.alert = {
                    type: 'error',
                    message: error.error?.message || '❌ Error al actualizar el perfil. Por favor, inténtalo de nuevo.'
                };
            }
        });
    }

    /**
     * Get role display name
     */
    getRoleName(role: string): string {
        const roleMap: Record<string, string> = {
            'admin': '🛡️ Administrador',
            'project_manager': '📁 Gestor de Proyectos',
            'writer': '✏️ Editor',
            'reader': '🔍 Lector'
        };
        return roleMap[role] || role;
    }
}
